import * as React from "react";
import AsyncSelect from "react-select/async";
import { WarehouseIcon, Package2, CheckCircle } from "lucide-react";
import { useTranslation } from "react-i18next";
import { Card, CardContent } from "@shared/components/ui/card";
import { Badge } from "@shared/components/ui/badge";
import { cn } from "@/lib/utils";
import * as productService from '@features/products/services/productService';

// Custom styles for react-select to match shadcn UI
const customStyles = {
  control: (provided, state) => ({
    ...provided,
    minHeight: "44px",
    border: "2px solid hsl(var(--border))",
    borderRadius: "6px",
    backgroundColor: "hsl(var(--background))",
    boxShadow: state.isFocused ? "0 0 0 2px hsl(var(--primary) / 0.2)" : "none",
    borderColor: state.isFocused ? "hsl(var(--primary))" : "hsl(var(--border))",
    "&:hover": {
      borderColor: "hsl(var(--primary) / 0.5)",
    },
    transition: "all 0.2s",
  }),
  singleValue: (provided) => ({
    ...provided,
    color: "hsl(var(--foreground))",
  }),
  placeholder: (provided) => ({
    ...provided,
    color: "hsl(var(--muted-foreground))",
  }),
  input: (provided) => ({
    ...provided,
    color: "hsl(var(--foreground))",
  }),
  menu: (provided) => ({
    ...provided,
    backgroundColor: "hsl(var(--popover))",
    border: "2px solid hsl(var(--border))",
    borderRadius: "6px",
    boxShadow: "0 10px 15px -3px hsl(var(--background) / 0.1), 0 4px 6px -2px hsl(var(--background) / 0.05)",
    zIndex: 1000,
  }),
  option: (provided, state) => ({
    ...provided,
    backgroundColor: state.isSelected
      ? "hsl(var(--primary) / 0.2)"
      : state.isFocused
      ? "hsl(var(--primary) / 0.1)"
      : "transparent",
    color: "hsl(var(--foreground))",
    cursor: "pointer",
    "&:hover": {
      backgroundColor: "hsl(var(--primary) / 0.1)",
    },
  }),
  loadingMessage: (provided) => ({
    ...provided,
    color: "hsl(var(--muted-foreground))",
  }),
  noOptionsMessage: (provided) => ({
    ...provided,
    color: "hsl(var(--muted-foreground))",
  }),
};

export function SelectProduct({ 
  product, 
  onSelectProduct, 
  isMulti = false, 
  products = [], 
  className 
}) {
  const { t } = useTranslation();
  const [selectedProductData, setSelectedProductData] = React.useState(
    isMulti ? products : product || null
  );

  // Load options function for react-select async
  const loadOptions = async (inputValue) => {
    try {
      const params = new URLSearchParams({
        name_q: inputValue || "",
        isActive: "true",
        limit: "50",
        page: "1",
      });
      const response = await productService.fetchProducts(params.toString());
      
      return (response?.data || []).map((product) => ({
        value: product._id,
        label: `${product.name} (${product.stock || 0} ${t("main.inStock")})`,
        product: product,
      }));
    } catch (error) {
      console.error("Error loading products:", error);
      return [];
    }
  };

  const handleChange = (selectedOption) => {
    if (isMulti) {
      const newProducts = selectedOption?.map((option) => option.product) || [];
      setSelectedProductData(newProducts);
      onSelectProduct(newProducts);
    } else {
      const newProduct = selectedOption?.product || null;
      setSelectedProductData(newProduct);
      onSelectProduct(newProduct);
    }
  };

  const selectedOptions = React.useMemo(() => {
    if (isMulti) {
      return (selectedProductData || []).map((product) => ({
        value: product._id,
        label: `${product.name} (${product.stock || 0} ${t("main.inStock")})`,
        product: product,
      }));
    } else {
      return selectedProductData ? {
        value: selectedProductData._id,
        label: `${selectedProductData.name} (${selectedProductData.stock || 0} ${t("main.inStock")})`,
        product: selectedProductData,
      } : null;
    }
  }, [selectedProductData, isMulti, t]);

  return (
    <div className={cn("space-y-4", className)}>
      <AsyncSelect
        isMulti={isMulti}
        value={selectedOptions}
        onChange={handleChange}
        loadOptions={loadOptions}
        placeholder={t("action.select", {
          entity: isMulti ? t("products.products") : t("products.product"),
        })}
        noOptionsMessage={({ inputValue }) =>
          t("main.notFound", {
            entity: isMulti ? t("products.products") : t("products.product"),
          })
        }
        loadingMessage={() => t("main.loading")}
        styles={customStyles}
        isClearable
        cacheOptions
        defaultOptions
        className="w-full"
        classNamePrefix="react-select"
      />

      {/* Single Product Display */}
      {!isMulti && selectedProductData && (
        <Card className="border-border bg-card/50 ring-1 ring-primary/10">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-primary/10 rounded-full">
                <Package2 className="h-5 w-5 text-primary" />
              </div>
              <div className="flex-1">
                <div className="flex items-center gap-2 mb-1">
                  <h4 className="font-semibold text-foreground">
                    {selectedProductData.name}
                  </h4>
                  <CheckCircle className="h-4 w-4 text-primary" />
                </div>
                <div className="flex items-center gap-2">
                  <Badge variant="secondary" className="text-xs">
                    {t("products.stock")}: {selectedProductData.stock || 0}
                  </Badge>
                  {selectedProductData.price && (
                    <Badge variant="outline" className="text-xs">
                      {selectedProductData.price} {t("main.currency")}
                    </Badge>
                  )}
                </div>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Multi Products Display */}
      {isMulti && selectedProductData && selectedProductData.length > 0 && (
        <div className="space-y-2">
          <div className="flex items-center justify-between">
            <h4 className="text-sm font-medium text-foreground">
              {t("products.selectedProducts")} ({selectedProductData.length}):
            </h4>
          </div>
          <div className="grid gap-2">
            {selectedProductData.map((product) => (
              <Card key={product._id} className="border-border bg-card/50 ring-1 ring-primary/10">
                <CardContent className="p-3">
                  <div className="flex items-center gap-3">
                    <div className="p-2 bg-primary/10 rounded-full">
                      <Package2 className="h-4 w-4 text-primary" />
                    </div>
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-1">
                        <h5 className="font-medium text-foreground text-sm">
                          {product.name}
                        </h5>
                        <CheckCircle className="h-3 w-3 text-primary" />
                      </div>
                      <div className="flex items-center gap-2">
                        <Badge variant="secondary" className="text-xs">
                          {t("products.stock")}: {product.stock || 0}
                        </Badge>
                        {product.price && (
                          <Badge variant="outline" className="text-xs">
                            {product.price} {t("main.currency")}
                          </Badge>
                        )}
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
